import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/surcharges',
  component: SurchargesPage,
})

type Surcharge = NonNullable<Awaited<ReturnType<typeof api.listSurcharges>>>['surcharges'][number]

const TYPE_LABELS: Record<string, string> = {
  sunday: 'Sonntag',
  holiday: 'Feiertag',
  night: 'Nacht',
  saturday: 'Samstag',
  overtime: 'Überstunden',
  custom: 'Sonstiges',
}

function SurchargesPage() {
  const [editing, setEditing] = useState<Surcharge | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['surcharges'],
    queryFn: () => api.listSurcharges(),
    enabled: !!meData?.user,
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') {
    return (
      <PersonnelLayout title="Zuschläge">
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
          <p className="text-text-secondary">Zuschläge sind nur für Admins sichtbar.</p>
        </div>
      </PersonnelLayout>
    )
  }

  const surcharges = data?.surcharges ?? []

  return (
    <PersonnelLayout
      title="Zuschläge"
      description="Sonn-, Feiertags-, Nacht-, Überstunden-Zuschläge konfigurieren. Wirken sich auf die Lohnauswertung aus, Zuweisung pro Mitarbeiter."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neuer Zuschlag in der Bottom-Bar —
          derselbe Handler wie der Kopf-Button. */}
      <MobileBottomSlots>
        <BarAccentPlus ariaLabel="Neuer Zuschlag" onClick={() => setCreateOpen(true)} />
      </MobileBottomSlots>

      <div className="space-y-6">
        {/* <640 übernimmt die Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
        <div className="flex phone:hidden justify-end">
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Neuer Zuschlag
          </Button>
        </div>

        {isLoading ? (
          <p className="text-text-secondary">Lade …</p>
        ) : surcharges.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
            <p className="text-text-secondary">
              Keine Zuschläge angelegt. Beispiel: „Sonntag 50 %“, „Nacht 22–06, 25 %“.
            </p>
          </div>
        ) : (
          <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
            {surcharges.map((s, i) => (
              <SurchargeRow
                key={s.id}
                surcharge={s}
                isLast={i === surcharges.length - 1}
                onEdit={() => setEditing(s)}
              />
            ))}
          </ul>
        )}
      </div>

      <SurchargeFormDialog
        open={createOpen || !!editing}
        surcharge={editing}
        onClose={() => {
          setCreateOpen(false)
          setEditing(null)
        }}
      />
    </PersonnelLayout>
  )
}

function SurchargeRow({
  surcharge,
  isLast,
  onEdit,
}: {
  surcharge: Surcharge
  isLast: boolean
  onEdit: () => void
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteSurcharge(surcharge.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surcharges'] })
      notify.success('Zuschlag gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const conditions: string[] = []
  if (surcharge.appliesOnWeekdays?.length) {
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
    conditions.push(surcharge.appliesOnWeekdays.map((d) => days[d]).join('+'))
  }
  if (surcharge.appliesOnHoliday) conditions.push('Feiertag')
  if (surcharge.activeFromTime && surcharge.activeUntilTime)
    conditions.push(`${surcharge.activeFromTime}–${surcharge.activeUntilTime}`)
  if (surcharge.thresholdHoursDaily) conditions.push(`ab ${surcharge.thresholdHoursDaily}h/Tag`)
  if (surcharge.thresholdHoursWeekly) conditions.push(`ab ${surcharge.thresholdHoursWeekly}h/Woche`)

  return (
    <li
      className={`group grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-5 py-4 transition-colors hover:bg-bg-overlay/40 ${
        !isLast ? 'border-b border-border-subtle' : ''
      } ${!surcharge.active ? 'opacity-60' : ''}`}
    >
      <div>
        <p className="text-sm font-medium text-text-primary">{surcharge.name}</p>
        <p className="text-xs text-text-tertiary">
          {TYPE_LABELS[surcharge.type] ?? surcharge.type}
          {conditions.length > 0 && ` · ${conditions.join(' · ')}`}
          {!surcharge.stackable && ' · nicht-kumulierbar'}
        </p>
      </div>
      <p className="font-mono text-sm text-accent tabular-nums">
        +{Number(surcharge.percentage).toFixed(0)}&nbsp;%
      </p>
      <span />
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label="Bearbeiten"
          className="text-text-tertiary hover:text-accent"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={async () => {
            const ok = await confirm({
              title: `Zuschlag „${surcharge.name}“ löschen?`,
              variant: 'danger',
              confirmLabel: 'Löschen',
            })
            if (ok) remove.mutate()
          }}
          aria-label="Löschen"
          className="text-text-tertiary hover:text-danger"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  )
}

function SurchargeFormDialog({
  open,
  surcharge,
  onClose,
}: {
  open: boolean
  surcharge: Surcharge | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isEdit = !!surcharge
  const [name, setName] = useState('')
  const [type, setType] = useState('custom')
  const [percentage, setPercentage] = useState('25')
  const [activeFromTime, setActiveFromTime] = useState('')
  const [activeUntilTime, setActiveUntilTime] = useState('')
  const [appliesOnHoliday, setAppliesOnHoliday] = useState(false)
  const [appliesOnSunday, setAppliesOnSunday] = useState(false)
  const [thresholdDaily, setThresholdDaily] = useState('')
  const [thresholdWeekly, setThresholdWeekly] = useState('')
  const [stackable, setStackable] = useState(true)
  const [exclusiveGroup, setExclusiveGroup] = useState('')

  useEffect(() => {
    if (!open) return
    if (surcharge) {
      setName(surcharge.name)
      setType(surcharge.type)
      setPercentage(surcharge.percentage)
      setActiveFromTime(surcharge.activeFromTime ?? '')
      setActiveUntilTime(surcharge.activeUntilTime ?? '')
      setAppliesOnHoliday(surcharge.appliesOnHoliday)
      setAppliesOnSunday(surcharge.appliesOnWeekdays?.includes(0) ?? false)
      setThresholdDaily(surcharge.thresholdHoursDaily ?? '')
      setThresholdWeekly(surcharge.thresholdHoursWeekly ?? '')
      setStackable(surcharge.stackable)
      setExclusiveGroup(surcharge.exclusiveGroup ?? '')
    } else {
      setName('')
      setType('custom')
      setPercentage('25')
      setActiveFromTime('')
      setActiveUntilTime('')
      setAppliesOnHoliday(false)
      setAppliesOnSunday(false)
      setThresholdDaily('')
      setThresholdWeekly('')
      setStackable(true)
      setExclusiveGroup('')
    }
  }, [open, surcharge])

  const save = useMutation({
    mutationFn: () => {
      const data = {
        name,
        type,
        percentage: Number(percentage),
        appliesOnWeekdays: appliesOnSunday ? [0] : undefined,
        appliesOnHoliday,
        activeFromTime: activeFromTime || undefined,
        activeUntilTime: activeUntilTime || undefined,
        thresholdHoursDaily: thresholdDaily ? Number(thresholdDaily) : undefined,
        thresholdHoursWeekly: thresholdWeekly ? Number(thresholdWeekly) : undefined,
        stackable,
        exclusiveGroup: exclusiveGroup || undefined,
      }
      if (isEdit && surcharge) return api.updateSurcharge(surcharge.id, data)
      return api.createSurcharge(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surcharges'] })
      notify.success(isEdit ? 'Zuschlag aktualisiert' : 'Zuschlag angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    save.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={isEdit ? 'Zuschlag bearbeiten' : 'Neuer Zuschlag'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="sc-name">Name</Label>
            <Input
              id="sc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Sonntags-Zuschlag"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc-type">Typ</Label>
            <Select id="sc-type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="custom">Sonstiges</option>
              <option value="sunday">Sonntag</option>
              <option value="holiday">Feiertag</option>
              <option value="night">Nacht</option>
              <option value="saturday">Samstag</option>
              <option value="overtime">Überstunden</option>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sc-pct">Zuschlag (%)</Label>
          <Input
            id="sc-pct"
            type="number"
            min="0"
            max="500"
            step="5"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>Aktivierung</Label>
          <div className="space-y-2 rounded-xl border border-border-subtle p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={appliesOnSunday}
                onChange={(e) => setAppliesOnSunday(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              <span>Gilt sonntags</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={appliesOnHoliday}
                onChange={(e) => setAppliesOnHoliday(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              <span>Gilt an Feiertagen</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="time"
                value={activeFromTime}
                onChange={(e) => setActiveFromTime(e.target.value)}
                placeholder="von (optional)"
              />
              <Input
                type="time"
                value={activeUntilTime}
                onChange={(e) => setActiveUntilTime(e.target.value)}
                placeholder="bis"
              />
            </div>
            <p className="text-xs text-text-tertiary">
              Zeitfenster für Nacht-Zuschlag (z.B. 22:00–06:00). Wenn von &gt; bis, läuft über
              Mitternacht.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="sc-th-d">ab N Std/Tag</Label>
            <Input
              id="sc-th-d"
              type="number"
              min="0"
              step="0.5"
              value={thresholdDaily}
              onChange={(e) => setThresholdDaily(e.target.value)}
              placeholder="optional"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc-th-w">ab N Std/Woche</Label>
            <Input
              id="sc-th-w"
              type="number"
              min="0"
              step="0.5"
              value={thresholdWeekly}
              onChange={(e) => setThresholdWeekly(e.target.value)}
              placeholder="optional"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={stackable}
            onChange={(e) => setStackable(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          <span>Mit anderen Zuschlägen kumulierbar</span>
        </label>

        {!stackable && (
          <div className="space-y-2">
            <Label htmlFor="sc-eg">Exklusiv-Gruppe</Label>
            <Input
              id="sc-eg"
              value={exclusiveGroup}
              onChange={(e) => setExclusiveGroup(e.target.value)}
              placeholder="z.B. weekend (höchster Wert in Gruppe greift)"
            />
          </div>
        )}
      </form>
    </SidePanel>
  )
}
